import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, createSearchParams, useLocation } from 'react-router-dom';
import _ from 'lodash';
import { useSnapshot } from 'valtio';
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Label,
  FormGroup,
  Form,
  Input,
  Table,
  Row,
  Col,
  Spinner,
  UncontrolledTooltip,
  Tooltip,
  TabPane,
} from 'reactstrap';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose, faCropSimple, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { optionizeAll } from 'helpers/select';
import PortfolioActions from 'page-partials/portfolio-analysis/BuildPortfolio/PortfolioActions';
import { useArrayState } from 'components/CustomHooks';
import { cn } from 'lib/utils';
import { optimizationState } from '../state';
import {
  HundredInfoTooltip,
  CopyPortfolioButton,
  selectOptions,
  getSum,
  isHundred,
  trimData,
  parseNumericValue,
  defaultNulls,
  defaultZeros,
  defaultHundreds,
  defaultGroups,
  DEFAULT_PORTFOLIO_1,
} from './Shared';

function Portfolio({ productMeta, confirm, session, extended = false }) {
  const snap = useSnapshot(optimizationState);
  const maxItems = session?.permissions['pa-portfolio-optimization-max-items'] ?? 1;

  const groupOptions = Array.from({ length: maxItems }, (_, i) => ({
    value: `Group ${i + 1}`,
    label: `Group ${i + 1}`,
  }));

  useEffect(() => {
    const _products = [];
    const _databases = [];
    const _weights = [];

    for (let x = 0; x < snap.products.length; x++) {
      const product = snap.products[x];
      const database = snap.databases[x];
      const weight = snap.weights[x];

      if (product && database) {
        _products.push(product);
        _databases.push(database);
        _weights.push(weight);
      }
    }

    const isValid = isHundred(_weights);

    // optimizationState.products = _products;
    // optimizationState.databases = _databases;
    // optimizationState.weights = _weights;
    optimizationState.isValid = isValid;
  }, [snap.products, snap.databases, snap.weights]);

  const databaseOptions = useMemo(() => {
    if (!productMeta) return [];

    const keys = _.keys(productMeta);
    return optionizeAll(keys);
  }, [productMeta]);

  const handlePortfolioLoadSelected = useCallback(
    (portfolio) => {
      const products = [...snap.products];
      const databases = [...snap.databases];
      const defaultLength = products.length;

      const weights = Array(defaultLength).fill(0);
      const expectedReturns = Array(defaultLength).fill(0);
      const minWeights = Array(defaultLength).fill(0);
      const maxWeights = Array(defaultLength).fill(100);
      const groups = Array(defaultLength).fill('Group 1');

      for (const row of portfolio.data) {
        const index = products.findIndex((asset, i) => asset === row.asset && databases[i] === row.database);

        if (index > -1) {
          weights[index] = row.weight;
        } else {
          products.push(row.asset);
          databases.push(row.database);
          weights.push(row.weight);
          expectedReturns.push(0);
          minWeights.push(0);
          maxWeights.push(100);
          groups.push('Group 1');
        }
      }

      Object.assign(
        optimizationState,
        trimData(products, databases, weights, expectedReturns, minWeights, maxWeights, groups),
        { portName: portfolio.name },
      );
    },
    [snap.products, snap.databases, snap.weights, snap.expectedReturns, snap.minWeights, snap.maxWeights, snap.groups],
  );

  const handlePortfolioSaveSelected = (portfolio) => {
    optimizationState.portName = portfolio.name;
  };

  const handlePortfolioClearSelected = () => {
    optimizationState.weights = snap.weights.map(() => 0);
    optimizationState.portName = DEFAULT_PORTFOLIO_1;
  };

  const getWeightedArray = () => {
    const selected = snap.products.filter(Boolean).length;
    const eql = Math.round((100 / selected) * 100) / 100;

    let lastIndex = 0;
    const ret = snap.products.map((asset, index) => {
      if (asset) {
        lastIndex = index;
        return eql;
      }
      return 0;
    });

    const total = _.sum(ret);
    if (total < 100) {
      ret[lastIndex] += 100 - total;
    } else if (total > 100) {
      ret[lastIndex] -= total - 100;
    }

    ret[lastIndex] = Number(ret[lastIndex].toFixed(2));

    return ret;
  };

  if (!productMeta) return;

  let widthClass = '';
  if (extended) {
    let factorCount = 0;
    if (snap.returnAssumption === 'User Set') factorCount += 1;
    if (snap.assetConstraint) factorCount += 1;

    widthClass = factorCount === 2 ? 'tw-max-w-7xl' : factorCount === 1 ? 'tw-max-w-6xl' : 'tw-max-w-4xl';
  } else {
    widthClass = 'tw-max-w-2xl';
  }

  const swipeMinMaxWeights = (i) => {
    if (snap.minWeights[i] > snap.maxWeights[i]) {
      const minWeights = [...snap.minWeights];
      const maxWeights = [...snap.maxWeights];

      minWeights[i] = snap.maxWeights[i];
      maxWeights[i] = snap.minWeights[i];

      optimizationState.minWeights = minWeights;
      optimizationState.maxWeights = maxWeights;
    }
  };

  return (
    <div className={cn('tw-mx-auto', widthClass)}>
      <div className="tw-flex tw-justify-between">
        <Button
          className=""
          color="default"
          onClick={() => {
            confirm({
              content: 'Are you sure to reset all portfolios?',
              callback: (result) => {
                if (result != 'confirmed') return;

                optimizationState.products = defaultNulls.concat();
                optimizationState.databases = defaultNulls.concat();
                optimizationState.weights = defaultZeros.concat();
                optimizationState.expectedReturns = defaultZeros.concat();
                optimizationState.minWeights = defaultZeros.concat();
                optimizationState.maxWeights = defaultHundreds.concat();
                optimizationState.groups = defaultGroups.concat();
                optimizationState.portName = DEFAULT_PORTFOLIO_1;
              },
            });
          }}
        >
          Reset
        </Button>
        <div className=""></div>

        {maxItems > 0 ? (
          <CopyPortfolioButton
            isMulti={false}
            onClose={(data) => {
              if (data) {
                data = data.slice(0, maxItems);
                const products = [];
                const databases = [];
                const weights = [];

                for (let x = 0; x < data.length; x++) {
                  const item = data[x];
                  products.push(item.asset);
                  databases.push(item.database);
                  weights.push(item.weight1);
                }

                optimizationState.products = products;
                optimizationState.databases = databases;
                optimizationState.weights = weights;
                optimizationState.expectedReturns = Array(products.length).fill(0);
                optimizationState.minWeights = Array(products.length).fill(0);
                optimizationState.maxWeights = Array(products.length).fill(0);
                optimizationState.groups = Array(products.length).fill('Group 1');
              }
            }}
          />
        ) : (
          <div />
        )}
      </div>

      <Card className="">
        <CardBody>
          <Table hover striped bordered className="tw-table-auto">
            <thead>
              <tr>
                <th>SELECT ASSETS</th>
                <th>
                  <div className="tw-flex tw-justify-between">
                    <div>
                      <span className="tw-text-sm tw-font-bold tw-pb-0 tw-uppercase tw-border-0 tw-text-black tw-break-words tw-normal-case">
                        <div>{snap.portName}</div>
                        <Button
                          className="btn-round tw-py-1 tw-px-2 tw-text-xs"
                          size="sm"
                          color="primary"
                          onClick={() => {
                            optimizationState.weights = getWeightedArray();
                          }}
                        >
                          Equal Weight
                        </Button>
                      </span>
                    </div>
                    <PortfolioActions
                      name="1"
                      confirm={confirm}
                      products={snap.products}
                      databases={snap.databases}
                      weights={snap.weights}
                      onSaveSelected={handlePortfolioSaveSelected}
                      onLoadSelected={handlePortfolioLoadSelected}
                      onClearSelected={handlePortfolioClearSelected}
                      key="resetTime"
                    />
                  </div>
                </th>
                {extended && (
                  <>
                    {snap.returnAssumption === 'User Set' && <th>Expected Return %</th>}
                    {snap.assetConstraint && (
                      <>
                        <th>Min Weight %</th>
                        <th>Max Weight %</th>
                      </>
                    )}
                    <th>Group</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {snap.products.map((asset, i) => {
                const databaseAssetOptions = optionizeAll((snap.databases[i] && productMeta[snap.databases[i]]) || []);

                return (
                  <tr key={`${asset}-${i}`}>
                    <td>
                      <Select
                        className="react-select tw-min-w-[370px]"
                        name="databases"
                        placeholder="Select Database..."
                        value={databaseOptions.find((option) => option.value === snap.databases[i])}
                        onChange={(option) => {
                          const products = snap.products.concat();
                          const databases = snap.databases.concat();

                          databases[i] = option.value;
                          products[i] = null;

                          optimizationState.products = products;
                          optimizationState.databases = databases;
                        }}
                        options={databaseOptions}
                        styles={selectOptions}
                      />
                      <Select
                        className="react-select tw-min-w-[370px]"
                        name="products"
                        placeholder="Select Asset..."
                        value={databaseAssetOptions.find((option) => option.value === asset)}
                        onChange={(option) => {
                          const products = snap.products.concat();

                          products[i] = option.value;
                          optimizationState.products = products;
                        }}
                        // exclude the asset already taken
                        options={databaseAssetOptions.filter((option) => {
                          if (option.value === option) return true;
                          if (snap.products.includes(option.value)) return false;
                          return true;
                        })}
                        styles={selectOptions}
                      />
                    </td>
                    <td>
                      <FormGroup className="mb-0">
                        <Input
                          type="number"
                          min={0}
                          max={100}
                          value={snap.weights[i]}
                          onChange={(event) => {
                            const weights = snap.weights.concat();
                            weights[i] = parseNumericValue(event.target.value);
                            optimizationState.weights = weights;
                          }}
                          onWheel={(event) => {
                            event.target?.blur();
                            event.currentTarget?.blur();
                          }}
                        />
                      </FormGroup>
                    </td>
                    {extended && (
                      <>
                        {snap.returnAssumption === 'User Set' && (
                          <td>
                            <FormGroup className="mb-0">
                              <Input
                                type="number"
                                min={-100}
                                max={100}
                                value={snap.expectedReturns[i]}
                                onChange={(event) => {
                                  const expectedReturns = snap.expectedReturns.concat();
                                  expectedReturns[i] = event.target.value;
                                  optimizationState.expectedReturns = expectedReturns;
                                }}
                                onWheel={(event) => {
                                  event.target?.blur();
                                  event.currentTarget?.blur();
                                }}
                              />
                            </FormGroup>
                          </td>
                        )}
                        {snap.assetConstraint && (
                          <>
                            <td>
                              <FormGroup className="mb-0">
                                <Input
                                  type="number"
                                  min={0}
                                  max={100}
                                  value={snap.minWeights[i]}
                                  onChange={(event) => {
                                    const minWeights = [...snap.minWeights];
                                    minWeights[i] = parseNumericValue(event.target.value);
                                    optimizationState.minWeights = minWeights;
                                  }}
                                  onWheel={(event) => {
                                    event.target?.blur();
                                    event.currentTarget?.blur();
                                  }}
                                  onBlur={() => {
                                    swipeMinMaxWeights(i);
                                  }}
                                />
                              </FormGroup>
                            </td>
                            <td>
                              <FormGroup className="mb-0">
                                <Input
                                  type="number"
                                  min={0}
                                  max={100}
                                  value={snap.maxWeights[i]}
                                  onChange={(event) => {
                                    const maxWeights = snap.maxWeights.concat();
                                    maxWeights[i] = parseNumericValue(event.target.value);
                                    optimizationState.maxWeights = maxWeights;
                                  }}
                                  onWheel={(event) => {
                                    event.target?.blur();
                                    event.currentTarget?.blur();
                                  }}
                                  onBlur={() => {
                                    swipeMinMaxWeights(i);
                                  }}
                                />
                              </FormGroup>
                            </td>
                          </>
                        )}
                        <td>
                          <Select
                            className="react-select tw-min-w-[200px]"
                            name="databases"
                            placeholder="Select Group..."
                            value={groupOptions.find((option) => option.value === snap.groups[i])}
                            onChange={(option) => {
                              const groups = [...snap.groups];
                              groups[i] = option.value;
                              optimizationState.groups = groups;
                            }}
                            options={groupOptions}
                            styles={selectOptions}
                          />
                        </td>
                      </>
                    )}
                  </tr>
                );
              })}
              <tr key="sum">
                <td className={cn('tw-pt-[0.3rem] tw-pb-[0.3rem] tw-pl-[0.8rem] tw-font-semibold', {})}></td>
                <td
                  className={cn('tw-pt-[0.3rem] tw-pb-[0.3rem] tw-pl-[0.8rem] tw-font-semibold', {
                    'bg-danger': !isHundred(snap.weights),
                  })}
                  colSpan={5}
                >
                  <span className="tw-break-keep">{getSum(snap.weights)}%</span>{' '}
                  {!isHundred(snap.weights) && <HundredInfoTooltip id="hundred-portfolio1" />}
                </td>
              </tr>
            </tbody>
          </Table>

          {snap.databases.length < maxItems && (
            <Button
              className="btn-round"
              color="info"
              onClick={() => {
                const allowedNum = maxItems - snap.databases.length;
                if (allowedNum <= 0) return;

                optimizationState.products = snap.products.concat(...defaultNulls.slice(0, allowedNum));
                optimizationState.databases = snap.databases.concat(...defaultNulls.slice(0, allowedNum));
                optimizationState.weights = snap.weights.concat(...defaultZeros.slice(0, allowedNum));
                optimizationState.expectedReturns = snap.expectedReturns.concat(...defaultZeros.slice(0, allowedNum));
                optimizationState.minWeights = snap.minWeights.concat(...defaultZeros.slice(0, allowedNum));
                optimizationState.maxWeights = snap.maxWeights.concat(...defaultHundreds.slice(0, allowedNum));
                optimizationState.groups = snap.groups.concat(...defaultGroups.slice(0, allowedNum));
              }}
            >
              Add Assets
            </Button>
          )}
          {snap.databases.length > 5 && (
            <Button
              className="btn-round"
              color="danger"
              disabled={snap.databases.length <= 5}
              onClick={() => {
                let num = 5;
                if (snap.databases.length < 10) {
                  num = snap.databases.length - 5;
                }

                optimizationState.products = snap.products.slice(0, snap.products.length - num);
                optimizationState.databases = snap.databases.slice(0, snap.databases.length - num);
                optimizationState.weights = snap.weights.slice(0, snap.weights.length - num);
                optimizationState.expectedReturns = snap.expectedReturns.slice(0, snap.expectedReturns.length - num);
                optimizationState.minWeights = snap.minWeights.slice(0, snap.minWeights.length - num);
                optimizationState.maxWeights = snap.maxWeights.slice(0, snap.maxWeights.length - num);
                optimizationState.groups = snap.groups.slice(0, snap.groups.length - num);
              }}
            >
              Remove Assets
            </Button>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

export default Portfolio;
